import { cn } from '@sohanemon/utils';
import * as motion from 'motion/react-m';
import { useReducedMotion, type Variants } from 'motion/react';
import {
	type ComponentProps,
	type ReactNode,
	useEffect,
	useId,
	useRef,
	useState,
} from 'react';
import { MotionProvider } from './motion';

const VW = 205;
const VH = 259;
const PAD = 5;

// Diamond center and half diagonal (a 36px square rotated 45deg).
const CX = 98;
const CY = 125;
const R = 25.456;
const DIAMOND = `0,${-R} ${R},0 0,${R} ${-R},0`;

const CYCLE = 1.8; // idle loop length in seconds
const SLIDE_DUR = 0.55;
const DIAMOND_START = 0.5;
const SHINE_START = 1.05;
const SHINE_DUR = 0.5;

type Bezier = [number, number, number, number];
const EASE_OUT: Bezier = [0.16, 1, 0.3, 1];
const EASE_IN_OUT: Bezier = [0.65, 0, 0.35, 1];
const EASE_BACK: Bezier = [0.34, 1.56, 0.64, 1];

const UPPER_PATH =
	'M54.0007 124.999L1.5 125L99.5811 26.9578L151.5 26.9578L54.0007 124.999Z';
const LOWER_PATH =
	'M96.5 222.54L44.0001 222.54L141.581 124.96L193.713 124.96L96.5 222.54Z';
const SLIVER_PATH = 'M2.50025 124H194.668L192.672 126H0.5L2.50025 124Z';

type Pt = { x: number; y: number };

/* Slabs: fly in, then breathe outward on each pulse, snap home on settle. */
const slab = (from: Pt, breathe: Pt, delay: number): Variants => ({
	hidden: { x: from.x, y: from.y, opacity: 0 },
	intro: {
		x: 0,
		y: 0,
		opacity: 1,
		transition: {
			x: { duration: SLIDE_DUR, delay, ease: EASE_OUT },
			y: { duration: SLIDE_DUR, delay, ease: EASE_OUT },
			opacity: { duration: SLIDE_DUR / 2, delay, ease: 'linear' },
		},
	},
	idle: {
		x: [0, breathe.x, 0],
		y: [0, breathe.y, 0],
		transition: {
			x: { duration: CYCLE, ease: 'easeInOut', repeat: Infinity },
			y: { duration: CYCLE, ease: 'easeInOut', repeat: Infinity },
		},
	},
	settle: {
		x: 0,
		y: 0,
		opacity: 1,
		transition: { type: 'spring', stiffness: 320, damping: 20 },
	},
	rest: { x: 0, y: 0, opacity: 1 },
});

const UPPER = slab({ x: 70, y: -70 }, { x: 3, y: -3 }, 0);
const LOWER = slab({ x: -70, y: 70 }, { x: -3, y: 3 }, 0.1);

const sliver: Variants = {
	hidden: { opacity: 0 },
	intro: {
		opacity: 1,
		transition: { duration: 0.1, delay: DIAMOND_START + 0.05 },
	},
	rest: { opacity: 1 },
};

/* The dot: spring in, then pulse + quarter turn each cycle, pop on settle. */
const diamond: Variants = {
	hidden: { scale: 0, rotate: -35 },
	intro: {
		scale: 1,
		rotate: 0,
		transition: {
			type: 'spring',
			stiffness: 260,
			damping: 13,
			mass: 0.55,
			delay: DIAMOND_START,
		},
	},
	idle: {
		scale: [1, 1.14, 1],
		rotate: [0, 90],
		transition: {
			scale: { duration: CYCLE, ease: 'easeInOut', repeat: Infinity },
			rotate: { duration: CYCLE, ease: EASE_IN_OUT, repeat: Infinity },
		},
	},
	settle: {
		scale: [1, 1.4, 1],
		// NOTE: ends at rotate 0 to match rest exactly, avoiding a spin on settle -> rest.
		rotate: 0,
		transition: {
			scale: { duration: 0.6, times: [0, 0.4, 1], ease: [EASE_OUT, EASE_BACK] },
			rotate: { duration: 0.6, ease: EASE_OUT },
		},
	},
	rest: { scale: 1, rotate: 0 },
};

/* Sonar rings emitted by the dot. Two per cycle, the second a faint echo. */
const ripple = (delay: number, peak: number): Variants => ({
	hidden: { opacity: 0, scale: 1 },
	intro: { opacity: 0, scale: 1 },
	idle: {
		scale: [1, 3],
		opacity: [peak, 0],
		transition: {
			duration: CYCLE,
			ease: 'easeOut',
			repeat: Infinity,
			delay,
		},
	},
	settle: {
		scale: [1, 3.6],
		opacity: [peak * 1.4, 0],
		transition: { duration: 1, ease: EASE_OUT, delay },
	},
	rest: { opacity: 0, scale: 1 },
});

const RIPPLE_A = ripple(0, 0.55);
const RIPPLE_B = ripple(0.18, 0.28);

/* Shine band: one sweep on intro, one per cycle while idle, one on settle. */
const shine: Variants = {
	hidden: { x: -260, opacity: 0 },
	intro: {
		x: 260,
		opacity: [0, 1, 1, 0],
		transition: {
			x: { duration: SHINE_DUR, delay: SHINE_START, ease: EASE_OUT },
			opacity: {
				duration: SHINE_DUR,
				delay: SHINE_START,
				times: [0, 0.01, 0.99, 1],
				ease: 'linear',
			},
		},
	},
	idle: {
		x: [-260, -260, 260],
		opacity: [0, 0, 1, 1, 0],
		transition: {
			x: {
				duration: CYCLE,
				times: [0, 0.45, 1],
				ease: ['linear', EASE_OUT],
				repeat: Infinity,
			},
			opacity: {
				duration: CYCLE,
				times: [0, 0.45, 0.47, 0.95, 1],
				ease: 'linear',
				repeat: Infinity,
			},
		},
	},
	settle: {
		x: [-260, 260],
		opacity: [0, 1, 1, 0],
		transition: {
			x: { duration: 0.7, ease: EASE_OUT },
			opacity: { duration: 0.7, times: [0, 0.01, 0.99, 1], ease: 'linear' },
		},
	},
	rest: { x: -260, opacity: 0 },
};

type LogoRevealProps = ComponentProps<'div'> & {
	color?: string;
	/** While true the logo plays its idle loop. When false it settles. */
	loading?: boolean;
	/**
	 * Seconds between animation restarts while loading. Each restart replays
	 * the reveal from the beginning so the complete logo shows periodically.
	 * Pass 0 or null to disable.
	 * @default 15
	 */
	animationRestartDelay?: number | null;
	/** Fires once the settle animation has finished. */
	onSettled?: () => void;
};

export function SohanScriptLoader({
	color = 'var(--color-primary)',
	loading = true,
	animationRestartDelay = 8,
	onSettled,
	className,
	...props
}: LogoRevealProps) {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
	const clipId = `logo-clip-${uid}`;
	const shineId = `logo-shine-${uid}`;

	const reduced = useReducedMotion();
	// NOTE: reduced is sync; usePerformant resolves async (~1s) which would
	// stall the intro on a loader, so full motion keys off reduced-motion only.
	const isFullMotion = !reduced;
	const [introDone, setIntroDone] = useState(false);
	// NOTE: always play intro once, even when loading starts false, so the
	// final state is reached via a one-time animation instead of jumping
	// straight to settle from hidden (which left slabs/sliver invisible).
	const [runId, setRunId] = useState(0);
	// NOTE: while loading, restart the reveal from the beginning every
	// animationRestartDelay seconds so the complete logo shows periodically.
	useEffect(() => {
		if (
			!loading ||
			!isFullMotion ||
			!animationRestartDelay ||
			animationRestartDelay <= 0
		)
			return;
		const id = setInterval(() => {
			setRunId((n) => n + 1);
			setIntroDone(false);
		}, animationRestartDelay * 1000);
		return () => clearInterval(id);
	}, [loading, isFullMotion, animationRestartDelay]);

	const settledRef = useRef(onSettled);
	useEffect(() => {
		settledRef.current = onSettled;
	}, [onSettled]);
	const settledOnce = useRef(false);

	// Reduced motion has no settle animation, so report it right away.
	useEffect(() => {
		if (!isFullMotion && !loading && !settledOnce.current) {
			settledOnce.current = true;
			settledRef.current?.();
		}
	}, [isFullMotion, loading]);

	const handleSettleComplete = () => {
		if (settledOnce.current) return;
		settledOnce.current = true;
		settledRef.current?.();
	};

	const pulsing = !isFullMotion && loading;

	return (
		<MotionProvider>
			<div
				role="status"
				aria-busy={loading}
				aria-label={loading ? 'Loading' : 'Loaded'}
				{...props}
				className={cn(
					'relative mx-auto aspect-215/269 w-40 shrink-0',
					className,
				)}
			>
				{/* biome-ignore lint/a11y/noSvgWithoutTitle: decorative mark, the status root carries the label */}
				<svg
					viewBox={`${-PAD} ${-PAD} ${VW + PAD * 2} ${VH + PAD * 2}`}
					width="100%"
					height="100%"
					fill="none"
					aria-hidden
					className="absolute inset-0"
					style={
						isFullMotion && loading
							? { filter: 'drop-shadow(0 0 12px rgba(111,203,255,0.25))' }
							: undefined
					}
				>
					<defs>
						<clipPath id={clipId}>
							<polygon points={DIAMOND} transform={`translate(${CX} ${CY})`} />
							<path d={SLIVER_PATH} />
							<path d={UPPER_PATH} />
							<path d={LOWER_PATH} />
						</clipPath>
						<linearGradient id={shineId} x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="white" stopOpacity="0" />
							<stop offset="50%" stopColor="white" stopOpacity="0.55" />
							<stop offset="100%" stopColor="white" stopOpacity="0" />
						</linearGradient>
					</defs>

					<motion.g
						animate={{ opacity: pulsing ? [1, 0.5, 1] : 1 }}
						transition={{
							duration: 1.6,
							ease: 'easeInOut',
							repeat: pulsing ? Infinity : 0,
						}}
					>
						<LoaderAnimationStage
							key={loading ? 'loading' : 'settled'}
							runId={runId}
							loading={loading}
							isFullMotion={isFullMotion}
							introDone={introDone}
							onIntroComplete={() => setIntroDone(true)}
							onSettled={handleSettleComplete}
						>
							<motion.path d={UPPER_PATH} fill={color} variants={UPPER} />
							<motion.path d={LOWER_PATH} fill={color} variants={LOWER} />
							<motion.path d={SLIVER_PATH} fill={color} variants={sliver} />

							<g transform={`translate(${CX} ${CY})`}>
								{isFullMotion &&
									[RIPPLE_A, RIPPLE_B].map((variants, idx) => (
										<motion.polygon
											// biome-ignore lint/suspicious/noArrayIndexKey: variants are function
											key={idx}
											points={DIAMOND}
											stroke={color}
											strokeWidth={1.5}
											vectorEffect="non-scaling-stroke"
											style={{
												originX: 0.5,
												originY: 0.5,
												transformBox: 'fill-box',
											}}
											variants={variants}
										/>
									))}
								<motion.polygon
									points={DIAMOND}
									fill={color}
									style={{
										originX: 0.5,
										originY: 0.5,
										transformBox: 'fill-box',
									}}
									variants={diamond}
								/>
							</g>

							{isFullMotion && (
								<g clipPath={`url(#${clipId})`}>
									<motion.rect
										y={-260}
										width={110}
										height={800}
										fill={`url(#${shineId})`}
										style={{ rotate: 20 }}
										variants={shine}
									/>
								</g>
							)}
						</LoaderAnimationStage>
					</motion.g>
				</svg>
			</div>
		</MotionProvider>
	);
}

type LoaderAnimationStageProps = {
	children: ReactNode;
	introDone: boolean;
	isFullMotion: boolean;
	loading: boolean;
	onIntroComplete: () => void;
	onSettled: () => void;
	runId: number;
};

function LoaderAnimationStage({
	children,
	introDone,
	isFullMotion,
	loading,
	onIntroComplete,
	onSettled,
	runId,
}: LoaderAnimationStageProps) {
	const [settleDone, setSettleDone] = useState(false);
	const phase = !introDone
		? 'intro'
		: loading
			? 'idle'
			: settleDone
				? 'rest'
				: 'settle';

	const handleAnimationComplete = () => {
		if (!isFullMotion) return;
		if (phase === 'intro') onIntroComplete();
		else if (phase === 'settle') {
			setSettleDone(true);
			onSettled();
		}
	};

	return (
		<motion.g
			key={runId}
			initial={isFullMotion ? 'hidden' : false}
			animate={isFullMotion ? phase : 'rest'}
			onAnimationComplete={handleAnimationComplete}
		>
			{children}
		</motion.g>
	);
}
